import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Layout from './Layout';
import progressService from '../services/progressService';

const InvestmentType = () => {
  const navigate = useNavigate();
  const [investmentType, setInvestmentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvedEmail, setApprovedEmail] = useState('');

  const investmentOptions = [
    {
      value: 'individual',
      label: "Individual investor",
      description: "I'm investing as an individual person",
      icon: <PersonIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: '#6b7280' }} />,
    },
    {
      value: 'team',
      label: "Organization/Team",
      description: "I'm part of an investment team or organization",
      icon: <GroupIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: '#6b7280' }} />,
    },
    {
      value: 'advisor',
      label: "Investment Advisor",
      description: "I'm an advisor operating for my investor/client",
      icon: <BusinessCenterIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: '#6b7280' }} />,
    },
  ];

  useEffect(() => {
    const initializeComponent = async () => {
      const email = localStorage.getItem('approved-email');
      const token = localStorage.getItem('registration-token');
      
      if (!email || !token) {
        navigate('/');
        return;
      }
      
      setApprovedEmail(email);
      
      try {
        // First, check current progress to see if user has already completed this step
        const currentProgress = await progressService.getCurrentProgress(email);
        console.log('📊 InvestmentType: Current progress:', currentProgress);
        console.log('📊 InvestmentType: Current step:', currentProgress.registration_step);
        console.log('📊 InvestmentType: Investment type from progress:', currentProgress.form_data?.investment_type);
        
        // If user has already selected investment type and moved past this step, redirect them
        if (currentProgress.form_data?.investment_type && 
            currentProgress.registration_step !== 'investment_type_set') {
          console.log('⚠️ InvestmentType: User has already selected investment type, redirecting...');
          const nextRoute = currentProgress.form_data.investment_type === 'individual' ? '/subscription' : '/organization-setup';
          navigate(nextRoute);
          return;
        }
        
        // Load saved form data
        const savedData = await progressService.loadFormData(email, 'investment_type');
        
        if (savedData.investment_type) {
          setInvestmentType(savedData.investment_type);
          console.log('✅ InvestmentType: Loaded saved investment type:', savedData.investment_type);
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
        // Fallback to localStorage
        const savedType = localStorage.getItem('investment-type');
        if (savedType) {
          setInvestmentType(savedType);
          console.log('✅ InvestmentType: Loaded from localStorage:', savedType);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    initializeComponent();
  }, [navigate]);

  const handleBack = () => {
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!investmentType) {
      setError('Please select an investment type');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('💾 InvestmentType: Saving investment type:', investmentType);
      
      // Save using the progress service
      const result = await progressService.saveInvestmentType(approvedEmail, investmentType);
      
      console.log('📋 InvestmentType: Progress service result:', result);
      
      if (result.success) {
        console.log('✅ InvestmentType: Successfully saved investment type');
        
        // For individual investors, go directly to subscription
        // For other types, go to organization setup
        const nextRoute = investmentType === 'individual' ? '/subscription' : '/organization-setup';
        console.log('📍 InvestmentType: Navigating to:', nextRoute);
        navigate(nextRoute);
      } else {
        console.log('❌ InvestmentType: Progress service returned error:', result.error);
        throw new Error(result.error || 'Failed to save investment type');
      }
    } catch (err) {
      console.error('Error saving investment type:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: { xs: 0, sm: 2 },
          p: { xs: 2, sm: 3, md: 4 },
          boxShadow: { xs: 'none', sm: '0 1px 3px rgba(0, 0, 0, 0.1)' },
          maxWidth: '500px',
          margin: '0 auto',
          minHeight: { xs: '100vh', sm: 'auto' },
        }}
      >
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              p: 1,
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              backgroundColor: '#1f2937',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
              SC
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            mb: { xs: 3, sm: 4 },
            color: '#1f2937',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          What type of investor are you?
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <FormControl component="fieldset" sx={{ width: '100%', mb: { xs: 3, sm: 4 } }}>
            <RadioGroup
              value={investmentType}
              onChange={(e) => setInvestmentType(e.target.value)}
              sx={{ gap: { xs: 1.5, sm: 2 } }}
            >
              {investmentOptions.map((option) => (
                <Card
                  key={option.value}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    border: investmentType === option.value ? 2 : 1,
                    borderColor: investmentType === option.value ? 'primary.main' : '#e5e7eb',
                    backgroundColor: investmentType === option.value ? '#f8faff' : 'white',
                    borderRadius: { xs: 1, sm: 2 },
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: '#f8faff',
                    },
                  }}
                  onClick={() => setInvestmentType(option.value)}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                    <FormControlLabel
                      value={option.value}
                      control={
                        <Radio
                          sx={{
                            color: '#d1d5db',
                            p: { xs: 0.5, sm: 1 },
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box sx={{ mr: { xs: 1.5, sm: 2 } }}>
                            {option.icon}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 500,
                                color: '#1f2937',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                mb: 0.5,
                              }}
                            >
                              {option.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6b7280',
                                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                lineHeight: 1.4,
                              }}
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        margin: 0,
                        width: '100%',
                        alignItems: 'flex-start',
                      }}
                    />
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !investmentType}
            sx={{
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue'
            )}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default InvestmentType; 